<template>
    <div class="reservation-form">
        <div class="row">
            <div class="col-md-12">
                <Select 
                    v-model="selectedProduct"
                    :options="filteredProducts"
                    label="Produto"
                    name="product"
                    :getOptionLabel="getProductName"
                    :disabled="false"
                    :required="true"
                    @change="onProductChange"
                    v-validate="'required'"
                    :helperText="errors.first('product')"
                    :error="errors.has('product')"
                    :loading="productsLoading"
                    loadingText="Carregando produtos..."
                    ref="selectedProduct"
                />
            </div>
        </div>

        <div v-if="isDateSellingMode || isDateTimeSellingMode" class="row">
            <div v-if="isDateSellingMode || isDateTimeSellingMode" class="col-md-6">
                <Select 
                    v-model="selectedDate"
                    :options="dateOptions"
                    label="Data"
                    name="date"
                    :getOptionLabel="(option) => getDateOptionLabel(option.date)"
                    :disabled="isWithoutDateSellingMode || productsLoading || !selectedProduct"
                    :required="false"
                    @change="onDateChange"
                    v-validate="{ 'required': !isWithoutDateSellingMode }"
                    :helperText="errors.first('date')"
                    :error="errors.has('date')"
                    :loading="datesLoading"
                    loadingText="Carregando datas..."
                    ref="selectedDate"
                />
            </div>

            <div v-if="isDateTimeSellingMode" class="col-md-6">
                <Select 
                    v-model="selectedTime"
                    :options="hourOptions"
                    label="Horário"
                    name="time"
                    :disabled="isWithoutDateSellingMode || !isDateTimeSellingMode || !selectedDate"
                    :required="false"
                    @change="onTimeChange"
                    v-validate="{ 'required': isDateTimeSellingMode }"
                    :helperText="errors.first('time')"
                    :error="errors.has('time')"
                    ref="selectedTime"
                />
            </div>
        </div>

        <div v-if="availableTariffs.length > 0" class="row">
            <div class="col-md-12">
                <TariffSelector
                    v-model="selectedTariffs"
                    :appCode="appCode"
                    :product="selectedProduct"
                    :scheduling="selectedScheduling"
                    :time="selectedTime"
                    name="tariffs"
                    v-validate="'required'"
                    :helperText="errors.first('tariffs')"
                    :error="errors.has('tariffs')"
                    ref="selectedTariffs"
                />
            </div>
        </div>

        <div v-if="shouldSelectSeats" class="row">
            <div class="col-md-12 seats">
                <SeatSelector
                    v-model="selectedResources"
                    :appCode="appCode"
                    :product="selectedProduct"
                    :scheduling="selectedScheduling"
                    :time="selectedTime"
                    :duration="selectedDuration"
                    :tariffs="selectedTariffs"
                    name="seats"
                    v-validate="'seat-map-min-tariffs:selectedTariffs|required'"
                    :helperText="errors.first('seats')"
                    :error="errors.has('seats')"
                    :dashboardUrl="dashboardUrl"
                    :date="selectedDate"
                    :useDashboardSeatMap="useDashboardSeatMap"
                />
            </div>
        </div>
        
        <div class="row">
            <div class="offset-md-6 col-md-6 col-sm-12 summaryValues">
                <div v-if="exceedingTariffValue > 0" class="tariffValue">
                    <p class="label">Tarifas selecionadas:</p>
                    <p class="value">{{ formatValueCents(totalTariffValue) }}</p>
                </div>

                <div v-if="exceedingTariffValue > 0" class="exceedingTariffValue">
                    <p class="label">Reserva de lugar:</p>
                    <p class="value">{{ formatValueCents(exceedingTariffValue) }}</p>
                </div>

                <div class="totalValue">
                    <p class="label">Total:</p>
                    <p class="value">{{ formatValueCents(totalSummaryValue) }}</p>
                </div>
            </div>
        </div>

        <div class="row">
            <div class="col-md-6 offset-md-6">
                <div class="row">
                    <div class="col-md-6">
                        <Button 
                            primary
                            isOutlined
                            :onClick="onCancel"
                        >
                            Cancelar
                        </Button>
                    </div>

                    <div class="col-md-6">
                        <Button 
                            :disabled="selectedTariffAmountSum.length < 1"
                            primary
                            title="Selecione ao menos uma tarifa"
                            :onClick="handleAddButton"
                        >
                            Adicionar
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>

import moment from 'moment';

import Button from '../../../../Button';
import Select from '../../../../Select';
import TextInput from '../../../../TextInput';

import SeatSelector from './SeatSelector';

import SellerApi from '../../../../../services/SellerApi';
import TariffSelector from './TariffSelector.vue';

export default {
    components: { Button, Select, TextInput, SeatSelector, TariffSelector },
    props: {
        appCode: String,
        appId: String,
        enableExtendedCalendar: {
            type: Boolean,
            required: false,
            default: false
        },
        onCancel: {
            type: Function,
            default: () => {},
        },
        onSave: {
            type: Function,
            default: () => {},
        },
        dashboardUrl: {
            type: String,
            required: true,
        },
        useDashboardSeatMap: {
            type: Boolean,
            required: true,
        },
    },
    data(){
        return {
            datesLoading: false,
            productsLoading: false,
            products: [],
            schedulings: [],
            selectedProduct: null,
            selectedDate: null,
            selectedTime: null,
            selectedTariffs: [],
            selectedResources: [],
        };
    },
    computed: {
        dateOptions(){
            const dates = {};

            for (const sch of this.schedulings) {
                if (dates[sch.date]) {
                    dates[sch.date].schedulings.push(sch);
                } else {
                    dates[sch.date] = {
                        date: sch.date,
                        schedulings: [sch],
                    };
                }
            }

            return Object.values(dates);
        },
        filteredProducts(){
            return this.products.filter(product =>
                product.active &&
                product.tariff_groups.some(tariffGroup => tariffGroup.tariffs.length > 0)
            );
        },
        // Retrieve the available times from the selected date
        hourOptions() {
            if (!this.selectedDate || !this.isDateTimeSellingMode) return [];

            // return only available times
            const hours = {};
            this.selectedDate.schedulings.forEach(sch => {
                sch.occupationRates.tariffScheduleQuantities.forEach(tsq => {
                    tsq.occupationSchedule.forEach(os => {
                        if (os.occupation.availableAmount === null || os.occupation.availableAmount > 0) {
                            hours[os.minutes] = {
                                label: os.time,
                                value: os.minutes,
                            };
                        }
                    });
                });
            });

            return Object.values(hours);
        },
        isDateTimeSellingMode() {
            return this.selectedProduct?.selling_mode === 'with_date_and_time';
        },
        isDateSellingMode() {
            return this.selectedProduct?.selling_mode === 'with_date_only';
        },
        isWithoutDateSellingMode() {
            return this.selectedProduct?.selling_mode === 'without_date';
        },
        defaultTariffGroup() {
            return this.selectedProduct?.tariff_groups.find(group => group.is_default);
        },
        selectedScheduling() {
            if(!this.selectedDate || (!this.isDateSellingMode && !this.selectedTime)) {
                return null;
            }

            return this.selectedDate.schedulings.find(sch => this.isDateSellingMode || (this.selectedTime && sch.availableScheduleTimes.some(st => st.minutes == this.selectedTime.value)));
        },
        availableTariffs() {
            if (this.isWithoutDateSellingMode) {
                return this.defaultTariffGroup?.tariffs
            }

            if(!this.selectedDate || (!this.isDateSellingMode && !this.selectedTime)) {
                return [];
            }

            return this.selectedScheduling.finalTariffGroup.tariffs;
        },
        selectedTariffAmountSum() {
            // retrieve the sum of the selected tariffs amount
            return this.selectedTariffs.reduce(
                (sum, tariff) => sum + tariff.amount,
                0
            );
        },

        shouldSelectSeats() {
            // form is unfilled
            if (
                !this.selectedProduct ||
                !this.selectedDate ||
                this.isWithoutDateSellingMode ||
                this.selectedTariffAmountSum === 0 ||
                (this.isDateTimeSellingMode && !this.selectedTime)
            ){
                return false;
            }

            if (this.availableTariffs.length === 0) {
                return false;
            }

            return this.selectedScheduling.requiresSeatSelection;
        },
        selectedDuration() {
            return this.selectedScheduling.durationMinutes;
        },
        totalTariffValue() {
            return this.selectedTariffs.reduce((sum, tariff) => sum + (tariff.tariff.price_cents ?? tariff.tariff.priceCents) * tariff.amount, 0);
        },
        minAllocatedValue() {
            return this.selectedResources.reduce((sum, resource) => {
                if (resource.category.complete_capacity_charge_type === 'fixed_value') {
                    return sum + (resource.capacity * resource.category.min_capacity_unit_cents)
                }

                if (resource.category.complete_capacity_charge_type === 'tariff_percent') {
                    return sum + (Math.ceil((resource.category.tariffPercentage / 100) * resource.category.tariff.price_cents) * resource.capacity)
                }

                return sum;
            }, 0);
        },
        exceedingTariffValue() {
            return Math.max(this.minAllocatedValue - this.totalTariffValue, 0);
        },
        totalSummaryValue() {
            return this.totalTariffValue + this.exceedingTariffValue;
        },
        hasAnyUnavailableAmount() {
            return this.$refs.selectedTariffs.hasAnyUnavailableAmount()
        }
    },
    methods: {
        getDateOptionLabel(date) {
            return moment(date).format('DD/MM/YYYY');
        },
        formatValueCents(value) {
            return (value/100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
        },
        handleAddButton() {
            this.$validator.validate().then(valid => {
                if (valid && !this.hasAnyUnavailableAmount) {
                    this.onSave({
                        product: this.selectedProduct,
                        date: this.selectedDate,
                        time: this.selectedTime,
                        tariffs: this.selectedTariffs,
                        resources: this.selectedResources,
                        totalTariffValue: this.totalTariffValue,
                        minAllocatedValue: this.minAllocatedValue,
                        exceedingTariffValue: this.exceedingTariffValue,
                        totalSummaryValue: this.totalSummaryValue,
                    });
                }
            });
        },
        onProductChange() {
            this.selectedDate = null;
            this.selectedTime = null;
            this.selectedTariffs = [];
            this.selectedResources = [];
        },
        onDateChange() {
            this.selectedTime = null;
            this.selectedTariffs = [];
            this.selectedResources = [];
        },
        onTimeChange() {
            this.selectedTariffs = [];
            this.selectedResources = [];
        },

        getSelectedTariffAmount(tariff){
            if (tariff === '') return 0;
            return parseInt(tariff ?? 0, 10);
        },

        getSellableProducts() {
            this.productsLoading = true;

            SellerApi.Products.sellableProducts(this.appCode)
                .then(data => {
                    this.productsLoading = false;
                    this.products = data
                })
                .catch(err => {
                    this.productsLoading = false;
                    console.error(err)
                });
        },
        getAvailableDates(product){
            this.datesLoading = true;

            const from = moment().format('YYYY-MM-DD');
            // Feature flag: PR-3379-planne-link-schedulings-quantity
            // If enabled, show 12 months ahead, otherwise show 6 months
            const monthsAhead = this.enableExtendedCalendar ? 12 : 6;
            const to = moment().add(monthsAhead, 'months').format('YYYY-MM-DD');

            const params = { from, to };

            SellerApi.Products.availableSchedulings(this.appCode, product.id, params)
                .then(data => {
                    this.datesLoading = false;

                    this.schedulings = data;
                })
                .catch(err => {
                    this.datesLoading = false;
                    console.error(err)
                });
        },
        getProductName(product){
            return this.appId === String(product.id_app) ? product.display_name : product.name;
        },
        validationMessage(key){
            const validation  = [];

            if(validation && validation[key]){
                return validation[key].join(', ');
            }

            return '';
        },
    },
    watch: {
        selectedProduct(newVal){
            if(newVal){
                this.getAvailableDates(newVal);
            }
        },
    },
    mounted() {
        this.getSellableProducts()
    }
}
</script>

<style scoped lang="scss">
.totalValue, .exceedingTariffValue, .tariffValue {
    display: flex;
    justify-content: space-between;
    font-size: 16px;
    text-align: right;

    .label, .value {
        margin-bottom: 0;
    }
}

.totalValue {
    font-weight: bold;
}

.seats {
    margin-bottom: 20px;
}

.summaryValues {
    margin-bottom: 20px;
}

</style>
